import { Cart } from "@commercetools/platform-sdk"
import { AxiosInstance } from "axios"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { useAxiosClient } from "../../../AxiosClient"
import { paymentApiUrl } from "../../../Config"
import { ErrorType } from "../../../error/ErrorType"
import { useAppDispatch, AppDispatch } from "../../../State"
import {
  GiftCardState,
  useGiftCardState,
  GiftCardStateI
} from "../../payment/GiftCardState"
import { PaymentProvider } from "../../payment/PaymentProviderState"
import {
  success,
  useValidationStrategy,
  CartValidationBase,
  cartSuccess
} from "../../validation/FormValidator"
import {
  error,
  ValidationResult,
  CartValidationResult,
  cartError
} from "../../validation/ValidationError"

interface GiftCardResponse {
  error?: { message: string }
}

export const useGiftCardValidation = (
  cart: Cart | undefined,
  currentPaymentProvider: PaymentProvider[]
) => {
  const { t } = useTranslation("checkout")
  const giftCardState = useGiftCardState()
  const client = useAxiosClient()
  const dispatch = useAppDispatch()
  let c = cart
  return useValidationStrategy(
    () => currentPaymentProvider.indexOf(PaymentProvider.GiftCard) > -1,
    {
      name: "GiftCardValidation",
      validate(): ValidationResult {
        return success()
      },
      refreshCart: newCart => (c = newCart),
      save(): ValidationResult {
        if (c && giftCardState.token) {
          return client
            .post<GiftCardResponse>(paymentApiUrl("giftcard/pay"), {
              cartId: c.id,
              paymentToken: giftCardState.token
            })
            .then(response => {
              if (response.status < 400) {
                dispatch(GiftCardState.actions.markUsed())
                return success()
              } else {
                return error(ErrorType.PaymentError, {
                  title: t("paymentFailedTitle"),
                  message: t("paymentFailedMessage")
                })
              }
            })
        } else {
          return error(ErrorType.PaymentError, {
            title: t("paymentFailedTitle"),
            message: t("paymentFailedMessage")
          })
        }
      }
    }
  )
}

export class GiftCardValidation extends CartValidationBase {
  private readonly t: TFunction
  private readonly giftcardState: GiftCardStateI
  private readonly dispatch: AppDispatch
  private readonly client: AxiosInstance
  private readonly skip: boolean

  constructor(
    t: TFunction,
    giftcardState: GiftCardStateI,
    client: AxiosInstance,
    dispatch: AppDispatch,
    currentPaymentProvider: PaymentProvider[]
  ) {
    super("GiftCardValidation")
    this.giftcardState = giftcardState
    this.dispatch = dispatch
    this.t = t
    this.client = client
    this.skip = currentPaymentProvider.indexOf(PaymentProvider.GiftCard) < 0
  }

  save(c: Cart): CartValidationResult {
    if (this.skip) {
      return cartSuccess(c)
    }
    if (c && this.giftcardState.token) {
      return this.client
        .post<GiftCardResponse>(paymentApiUrl("giftcard/pay"), {
          cartId: c.id,
          paymentToken: this.giftcardState.token
        })
        .then(response => {
          if (response.status < 400) {
            this.dispatch(GiftCardState.actions.markUsed())
            return cartSuccess(c)
          } else {
            return cartError(ErrorType.PaymentError, {
              title: this.t("paymentFailedTitle"),
              message: this.t("paymentFailedMessage")
            })
          }
        })
    } else {
      return cartError(ErrorType.PaymentError, {
        title: this.t("paymentFailedTitle"),
        message: this.t("paymentFailedMessage")
      })
    }
  }
}
